import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography
} from '@mui/material';
import { useAtom } from 'jotai';
import { orderBy, sumBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import HorizontalBarChart from '@/components/charts/HorizontalBarChart';
import type { PercentageBarDataType } from '@/components/PercentageBarWidget';
import PercentageBarChart from '@/components/PercentageBarWidget/PercentageBarWidget';
import { REPORTS_COLORS } from '@/constants/chartcolors.constants';
import REPORT_PARAGRAPHS from '@/constants/reportparagraph.constants';
import ReportParagraph from '@/features/ReportsTemplateParagraph/ReportsParagraph';
import { organizationsAtom } from '@/pages/Organizations';
import { REPORTS_TICKETS_HORIZONTAL_BAR_SERIES } from '@/pages/ReportsTemplates/ReportsTemplates.constants';
import {
  type RecommendationDTO,
  TICKET_THREAT_RESOLUTIONS
} from '@/pages/Ticket';
import type { Ticket } from '@/pages/Tickets';
import { formatTickets } from '@/pages/Tickets/Tickets.utils';
import { axiosClient } from '@/services/BaseApi.service';

import {
  reportsEndDateAtom,
  reportsOrgLabelAtom,
  reportsStartDateAtom
} from '../../ReportsTemplate.hooks';
import type { CasesGroupByDTO } from '../ReportsPage.types';
import useStyles from '../ReportsPages.styles';

const TicketsPage2 = ({
  tickets,
  ticketedCases
}: {
  tickets: Ticket[];
  ticketedCases: CasesGroupByDTO[];
}) => {
  const [startDate] = useAtom(reportsStartDateAtom);
  const [endDate] = useAtom(reportsEndDateAtom);
  const [orgLabel] = useAtom(reportsOrgLabelAtom);
  const [organizations] = useAtom(organizationsAtom);

  const [recs, setRecs] = useState<RecommendationDTO[]>([]);
  const { classes } = useStyles();

  const organization = useMemo(() => {
    return organizations.find((org) => org.label === orgLabel);
  }, [organizations, orgLabel]);

  const threatTickets = tickets?.filter(
    (ticket: Ticket) => ticket.ticket_type === 1
  );
  const threatTicketCount = threatTickets.length;

  const threatTicketResolutionGrouped = () => {
    const results = formatTickets(threatTickets, 'resolution_id' as const);
    let resolved = results.filter((result) => result.name !== 'null');

    resolved = resolved.map((result) =>
      result.name
        ? {
            ...result,
            name:
              TICKET_THREAT_RESOLUTIONS.find(
                (item) => item.id === Number(result.name)
              )?.text || '',
            color: REPORTS_COLORS.TICKETS
          }
        : result
    );

    return orderBy(resolved, (item) => item.data[0], 'desc').splice(0, 5);
  };

  // TODO: Using "completed" status as resolved time, this might need to be changed when we define what "resolved" means
  useEffect(() => {
    if (!organization || !startDate || !endDate) return;
    const fetchData = async () => {
      try {
        const recommendations = await axiosClient.get(
          `/organizations/${organization?.id}/recommendations?start_date=${startDate}&end_date=${endDate}`
        );

        setRecs(recommendations.data);
      } catch (error) {
        console.error('Error fetching resolution times:', error);
      }
    };
    fetchData();
  }, [organization, startDate, endDate]);

  const recommendationsData = useMemo(() => {
    return [
      {
        name: 'Completed',
        data: [
          recs.filter((rec: RecommendationDTO) => rec.completed === true).length
        ],
        color: '#4CAF50',
        type: 'count'
      },
      {
        name: 'In Progress',
        data: [
          recs.filter((rec: RecommendationDTO) => rec.completed === false)
            .length
        ],
        color: '#FF9800',
        type: 'count'
      }
    ];
  }, [recs]);

  const ticketCreationMetricsData = useMemo(() => {
    let sumOfTicketedCases = sumBy(ticketedCases, 'count');

    let nonCasesTickets = threatTicketCount - sumOfTicketedCases;
    if (threatTicketCount < sumOfTicketedCases) {
      nonCasesTickets = 0;
    }

    if (sumOfTicketedCases > threatTicketCount) {
      sumOfTicketedCases = threatTicketCount;
    }
    return [
      {
        name: 'Tickets Created from Cases',
        data: [sumOfTicketedCases],
        color: REPORTS_COLORS.CASES,
        type: 'count'
      },
      {
        name: 'Other Tickets',
        data: [nonCasesTickets],
        color: REPORTS_COLORS.NETWORK_EVENTS,
        type: 'count'
      }
    ];
  }, [threatTicketCount, ticketedCases]);

  return (
    <div className={classes.reportsPageContainer}>
      <Stack direction="row" spacing={2}>
        <Stack direction="column" sx={{ width: '60%' }}>
          <div
            data-testid="closed-threat-tickets-by-resolution"
            style={{ height: 275, width: '100%' }}
          >
            <Typography className={classes.reportsPreviewSubTitle}>
              Closed Threat Tickets by Resolution
            </Typography>
            <HorizontalBarChart
              height={225}
              data={threatTicketResolutionGrouped()}
              series={REPORTS_TICKETS_HORIZONTAL_BAR_SERIES}
            />
          </div>
          <Typography sx={{ fontSize: 12, marginTop: '-15px !important' }}>
            {REPORT_PARAGRAPHS.threatTicketsResolutionDescription}
          </Typography>
          <Stack spacing={0.25}>
            <List data-testid="ticket-threat-resolutions-list">
              {TICKET_THREAT_RESOLUTIONS.map((resolution) => (
                <ListItem sx={{ padding: '0px' }} key={resolution.id}>
                  <ListItemText
                    primary={
                      <Typography
                        style={{
                          fontWeight: 'bold',
                          fontSize: 12,
                          lineHeight: 1.6
                        }}
                      >
                        <IconButton>
                          <FontAwesomeIcon
                            style={{
                              height: 6,
                              width: 6,
                              color: 'black'
                            }}
                            icon={faCircle}
                          />
                        </IconButton>
                        {resolution.text}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        sx={{
                          fontSize: 12,
                          lineHeight: 1.6,
                          paddingLeft: '24px !important'
                        }}
                        variant="body1"
                      >
                        {resolution.sub_text}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Stack>
        </Stack>
        <Box
          sx={{
            backgroundColor: '#D1E2D5',
            width: 2,
            height: '100%'
          }}
        />
        <Stack
          spacing={3}
          className={classes.threatTicketsSmallColumn}
          direction="column"
          sx={{ width: '40%' }}
        >
          <div
            style={{ height: 140, width: '100%' }}
            className={classes.reportsChartContainer}
            data-testid="ticket-creation-metrics"
          >
            <Typography className={classes.reportsPreviewSubTitle}>
              Ticket Creation Metrics
            </Typography>
            <PercentageBarChart
              data={ticketCreationMetricsData as PercentageBarDataType[]}
            />
          </div>
          <ReportParagraph fontSize="12px">
            {REPORT_PARAGRAPHS.threatTicketsCreationMetrics}
          </ReportParagraph>
          <div
            style={{ height: 140, width: '100%' }}
            className={classes.reportsChartContainer}
            data-testid="recommendations"
          >
            <Typography className={classes.reportsPreviewSubTitle}>
              Recommendations
            </Typography>
            <PercentageBarChart
              data={recommendationsData as PercentageBarDataType[]}
            />
          </div>
          <ReportParagraph fontSize="12px">
            {REPORT_PARAGRAPHS.threatTicketsRecommendations}
          </ReportParagraph>
        </Stack>
      </Stack>
    </div>
  );
};

export default TicketsPage2;
